import React from "react";
import {
  Phone, Radar, Gauge, Bell, ShieldCheck, Brain, Activity,
  Zap, Sparkles, Fingerprint, Lock, Headphones, CheckCircle2
} from "lucide-react";
import { Button } from "./ui/button";
import { FEATURES, BENEFITS, COMPANY } from "../mock/data";

const featureIcons = [Radar, Gauge, Bell, Lock, Brain, Activity];
const benefitIcons = [ShieldCheck, Zap, Sparkles, Fingerprint, Brain, Headphones];

export function FeaturesSection() {
  return (
    <section id="features" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">Features</span>
          <h2 className="section-title mt-4 text-3xl md:text-4xl font-extrabold text-slate-900">
            Light on Your Phone. <span className="text-emerald-600">Heavy on Protection.</span>
          </h2>
          <p className="mt-3 text-slate-600">Experience the perfect balance of powerful protection and minimal resource usage.</p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = featureIcons[i] || ShieldCheck;
            return (
              <div key={f.title} className="feature-card rounded-2xl border border-slate-200 bg-white p-6">
                <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-slate-900 text-lg">{f.title}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <p className="text-slate-600">Questions about features? Speak to an expert for a quick walkthrough.</p>
          <Button asChild className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6">
            <a href={`tel:${COMPANY.phoneRaw}`}>
              <Phone className="h-4 w-4 mr-2" /> Call {COMPANY.phone}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

export function BenefitsSection() {
  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-emerald-50/40 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-emerald-700 bg-white border border-emerald-200 rounded-full px-3 py-1">Why {COMPANY.brand}</span>
          <h2 className="section-title mt-4 text-3xl md:text-4xl font-extrabold text-slate-900">
            Why {COMPANY.brand} Stands Out
          </h2>
          <p className="mt-3 text-slate-600">Built for the modern Android user who values both security and performance.</p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => {
            const Icon = benefitIcons[i] || ShieldCheck;
            return (
              <div key={b.title} className="feature-card rounded-2xl border border-slate-200 bg-white p-6 h-full">
                <div className="h-11 w-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-slate-900 text-lg">{b.title}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{b.desc}</p>
                <ul className="mt-4 space-y-2">
                  {b.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
